import { ColDef, ICellEditorParams } from "ag-grid-community";
import _ from "lodash";
import moment from "moment";
import ImpactsApi, { ImpactsApi as IImpactsApi } from "../../../../../services/api/v2/impacts/Impacts.api";
import GridToastService from "../../../../../services/local/gridToastService/GridToastService";
import ProgressIndicatorModel from "../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import I18n from "../../../../../core/localization/I18n";
import { defaultDateTimeString, Enums } from "../../../../../enums";
import { laterDateComparator } from "../../../../../core/grids/GridHelpers";
import CustomFieldsApi from "../../../../../services/api/v2/customFields/CustomFields.api";
import { SimpleTextColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/SimpleTextColumn/SimpleTextColumn_builder";
import { SIMPLE_TEXT_COLUMN_CONFIG } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/SimpleTextColumn/SimpleTextColumn_config";
import { BASE_FILTER_CONFIG } from "../../../../../apps/grids/builder/directorBuilder/columns/baseColumn/BaseColumn_config";
import { BaseGridColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/grids/base/BaseGridColumnBuilder";
import {
  CommonColDefFieldNamesEnum,
  ImpactColDefFieldNamesEnum
} from "../../../../../apps/grids/builder/enums/AgGridColDefFieldNameEnum";
import { RefNumberColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/RefNumberColumn/RefNumberColumn_builder";
import {
  REF_NUMBER_COLUMN_CONFIG,
  REF_NUMBER_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/RefNumberColumn/RefNumberColumn_config";
import { NameColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/NameColumn/NameColumn_builder";
import {
  NAME_COLUMN_CONFIG,
  NAME_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/NameColumn/NameColumn_config";
import {
  AUTOCOMPLETE_COLUMN_CONFIG,
  AUTOCOMPLETE_FILTER_CONFIG,
  mapImpactLevels,
  mapProgressStatuses
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/AutocompleteColumn_config";
import { AutocompleteColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/AutocompleteColumn_builder";
import {
  PROGRESS_STATUS_COLUMN_CONFIG,
  PROGRESS_STATUS_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/progressStatus/ProgressStatusColumn_config";
import { DateColumnBuilder } from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/DateColumn/DateColumn_builder";
import {
  DATE_COLUMN_CONFIG,
  DATE_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/DateColumn/DateColumn_config";
import {
  OWNER_NAME_COLUMN_CONFIG,
  OWNER_NAME_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/columns/commonColumns/AutocompleteColumn/ownerName/OwnerNameColumn_config";
import {
  IMPACT_LEVEL_COLUMN_CONFIG,
  IMPACT_LEVEL_FILTER_CONFIG
} from "../../../../../apps/grids/builder/directorBuilder/grids/ImpactsGrid/columns/ImpactLevelColumn/ImpactLevelColumn_config";
import { ImpactField } from "../../../../../apps/grids/builder/directorBuilder/utils/GridFields";
import { ChangePages } from "../../../../../apps/change/routes/pages";

export interface ImpactsGridDashboardColumnBuilderProps {
  canEdit: boolean;
  organisationId: number;
  projectId: number;
  userCanViewImpacts: boolean;
  columns: FP.Entities.IColumnDef[];
  projectTeamMembers: any[];
  onFieldUpdate: () => void;
}

export class ImpactsGridDashboardColumnBuilder extends BaseGridColumnBuilder {
  gridColumns: Dictionary<ColDef>;
  gridToastService = GridToastService;
  httpProgress = ProgressIndicatorModel;
  gridProps: ImpactsGridDashboardColumnBuilderProps;
  impactsProvider: IImpactsApi = ImpactsApi;
  columnDefs: Dictionary<() => ColDef>;
  organisationId: number;
  projectId: number;
  projectTeamMembers: any[];
  reloadData: () => void;

  constructor(gridProps: ImpactsGridDashboardColumnBuilderProps) {
    super(ImpactsApi.updateField, gridProps.organisationId, gridProps.projectId, gridProps.canEdit);
    this.gridProps = gridProps;
    this.organisationId = gridProps.organisationId;
    this.projectId = gridProps.projectId;
    this.projectTeamMembers = gridProps.projectTeamMembers;
    this.reloadData = gridProps.onFieldUpdate;
    this.init();
  }

  private init = () => {
    this.columnDefs = {
      [CommonColDefFieldNamesEnum.RefNumber]: () => this.buildRefNumberColumn(),
      [CommonColDefFieldNamesEnum.Name]: () => this.buildNameColumn(),
      [CommonColDefFieldNamesEnum.ProjectName]: () => this.buildProjectNameColumn(),
      [CommonColDefFieldNamesEnum.ProgrammeName]: () => this.buildProgrammeNameColumn(),
      [CommonColDefFieldNamesEnum.Owner]: () => this.buildOwnerColumn(),
      [CommonColDefFieldNamesEnum.ProgressStatus]: () => this.buildProgressStatusColumn(),
      [ImpactColDefFieldNamesEnum.ImpactLevel]: () => this.buildImpactLevelColumn(),
      [CommonColDefFieldNamesEnum.StartDate]: () => this.buildStartDateColumn(),
      [CommonColDefFieldNamesEnum.EndDate]: () => this.buildEndDateColumn()
    };
  };

  generateColumnDefs = (): ColDef[] => {
    let res: ColDef[] = [];

    if (this.gridProps.columns?.length === 0) {
      return _.map(this.columnDefs, e => {
        return e();
      });
    }
    // res.push(this.columnDefs[CommonColDefFieldNamesEnum.Selected]());
    this.gridProps.columns.forEach(e => {
      if (e.isCore) {
        let col = this.columnDefs[e.colName];
        if (typeof col === "function") {
          res.push(col());
        } else {
          throw new Error(`Typeof columnDef with name "${e.colName}" is not a function`);
        }
      } else {
        let col = this.buildDynamicColumn(
          e,
          this.gridProps.canEdit,
          this.updateDynamicFieldValueInt,
          this.updateDynamicFieldValueString
        );
        res.push(col);
      }
    });

    return res;
  };

  buildRefNumberColumn = () => {
    let model = new RefNumberColumnBuilder()
      .setColumnOptions(REF_NUMBER_COLUMN_CONFIG({ headerName: I18n.t("grids.refNo") }))
      .withLinkCell(
        (impactId, projectId) =>
          ChangePages.impacts.extendedView.generateLink(this.organisationId, projectId || this.projectId, impactId),
        CommonColDefFieldNamesEnum.RefNumber
      )
      .setFilterOptions(REF_NUMBER_FILTER_CONFIG);

    return model.generateColumnOptions();
  };
  buildNameColumn = () => {
    let model = new NameColumnBuilder()
      .setColumnOptions(NAME_COLUMN_CONFIG({ headerName: "Name", pinned: false }))
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .setFilterOptions(NAME_FILTER_CONFIG);

    if (this.gridProps.canEdit) {
      // make cell editable
      model.createValueSetterWithProjectId(this.updateName);
    }

    return model.generateColumnOptions();
  };
  buildProjectNameColumn = () => {
    let model = new SimpleTextColumnBuilder({
      field: CommonColDefFieldNamesEnum.ProjectName,
      headerName: I18n.t("grids.project")
    })
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: CommonColDefFieldNamesEnum.ProjectName,
          headerName: I18n.t("grids.project")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly(!this.canEdit)
      .setCellDataType("text")
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };
  buildProgrammeNameColumn = () => {
    let model = new SimpleTextColumnBuilder({
      field: CommonColDefFieldNamesEnum.ProgrammeName,
      headerName: I18n.t("grids.programme")
    })
      .setColumnOptions(
        SIMPLE_TEXT_COLUMN_CONFIG({
          field: CommonColDefFieldNamesEnum.ProgrammeName,
          headerName: I18n.t("grids.programme")
        })
      )
      .makeSelectable(this.canEdit)
      .makeEditable(false)
      .makeReadOnly(!this.canEdit)
      .setCellDataType("text")
      .setFilterOptions(BASE_FILTER_CONFIG);

    if (this.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildProgressStatusColumn = () => {
    const progressStatuses = mapProgressStatuses();
    let model = new AutocompleteColumnBuilder()
      .setColumnOptions(AUTOCOMPLETE_COLUMN_CONFIG({ field: "progressStatus", headerName: I18n.t("grids.status") }))
      .setFilterOptions({
        ...AUTOCOMPLETE_FILTER_CONFIG,
        ...PROGRESS_STATUS_FILTER_CONFIG
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellEditor()
      .setValueSetter(params => {
        if (!params.newValue) return false;

        params.data.progressStatus = +params.newValue.key;
        this.updateProgressStatus(params.data.id, params.newValue.key, params.data.projectId);

        return true;
      })
      .withCellRenderer(params => {
        if (!params.data.progressStatus || !progressStatuses) return null;
        const progress = progressStatuses.find(e => e.key === params.data.progressStatus + "");

        if (!progress) return "";
        return <>{progress.label}</>;
      })
      .setColumnOptions(PROGRESS_STATUS_COLUMN_CONFIG(progressStatuses));

    return model.generateColumnOptions();
  };

  buildStartDateColumn = () => {
    let model = new DateColumnBuilder()
      .setColumnOptions(
        DATE_COLUMN_CONFIG({
          field: CommonColDefFieldNamesEnum.StartDate,
          headerName: I18n.t("grids.startDate")
        })
      )
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withComparator()
      .withCellEditor(CommonColDefFieldNamesEnum.StartDate, "")
      .setValueFormatter(CommonColDefFieldNamesEnum.StartDate)
      .setValueGetterByFieldFn(CommonColDefFieldNamesEnum.StartDate)
      .setFilterOptions(DATE_FILTER_CONFIG)
      .setValueSetter(params => {
        return true;
      })
      .setColumnOptions({
        cellEditorParams: {
          field: CommonColDefFieldNamesEnum.StartDate,
          defaultDate: moment(),
          onDatepickerClick: (params: ICellEditorParams, e) => {
            if (!e) {
              params.data[CommonColDefFieldNamesEnum.StartDate] = defaultDateTimeString;
              return params.data[CommonColDefFieldNamesEnum.StartDate];
            }

            let startDate = moment(e);
            let endDate =
              params.data[CommonColDefFieldNamesEnum.EndDate] &&
              params.data[CommonColDefFieldNamesEnum.EndDate] !== defaultDateTimeString &&
              moment(params.data[CommonColDefFieldNamesEnum.EndDate]);

            if (!(startDate === endDate) && (!endDate || endDate > startDate)) {
              this.updateStartDate(params.data.id, startDate.toDate(), params.data.projectId);
              return e;
            }
            return params.data[CommonColDefFieldNamesEnum.StartDate];
          }
        }
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildEndDateColumn = () => {
    let model = new DateColumnBuilder()
      .setColumnOptions(
        DATE_COLUMN_CONFIG({ field: CommonColDefFieldNamesEnum.EndDate, headerName: I18n.t("grids.endDate") })
      )
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withComparator()
      .withCellEditor(CommonColDefFieldNamesEnum.EndDate, "")
      .setValueFormatter(CommonColDefFieldNamesEnum.EndDate)
      .setValueGetterByFieldFn(CommonColDefFieldNamesEnum.EndDate)
      .setFilterOptions(DATE_FILTER_CONFIG)
      .setValueSetter(params => {
        return true;
      })
      .setColumnOptions({
        cellEditorParams: {
          field: CommonColDefFieldNamesEnum.EndDate,
          defaultDate: moment(),
          onDatepickerClick: (params: ICellEditorParams, e) => {
            if (!e) {
              params.data[CommonColDefFieldNamesEnum.EndDate] = defaultDateTimeString;
              return params.data[CommonColDefFieldNamesEnum.EndDate];
            }

            let endDate = moment(e);
            let startDate =
              params.data[CommonColDefFieldNamesEnum.StartDate] &&
              params.data[CommonColDefFieldNamesEnum.StartDate] !== defaultDateTimeString &&
              moment(params.data[CommonColDefFieldNamesEnum.StartDate]);

            if (
              !(endDate === startDate) &&
              (!startDate || laterDateComparator(endDate.toDate(), startDate.toDate()) === true)
            ) {
              this.updateEndDate(params.data.id, endDate.toDate(), params.data.projectId);
              return e;
            }
            return params.data[CommonColDefFieldNamesEnum.EndDate];
          }
        }
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildOwnerColumn = () => {
    let model = new AutocompleteColumnBuilder()
      .setColumnOptions({
        ...AUTOCOMPLETE_COLUMN_CONFIG({ field: CommonColDefFieldNamesEnum.Owner, headerName: I18n.t("grids.owner") }),
        ...OWNER_NAME_COLUMN_CONFIG(CommonColDefFieldNamesEnum.Owner, this.projectTeamMembers)
      })
      .setFilterOptions({
        ...AUTOCOMPLETE_FILTER_CONFIG,
        ...OWNER_NAME_FILTER_CONFIG
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellEditor()
      .setValueGetter(e => {
        if (!e.data[CommonColDefFieldNamesEnum.Owner]) return;
        return `${e.data[CommonColDefFieldNamesEnum.Owner] ?? ""}`;
      })
      .setValueSetter(params => {
        if (!params.newValue) return false;

        params.data.ownerId = params.newValue.id;
        params.data[CommonColDefFieldNamesEnum.Owner] = `${params.newValue.firstName} ${params.newValue.lastName}`;

        this.updateOwnerName(params.data.id, params.newValue.id, params.data.projectId);
        return true;
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  buildImpactLevelColumn = () => {
    const impactLevels = mapImpactLevels();
    let model = new AutocompleteColumnBuilder()
      .setColumnOptions({
        ...IMPACT_LEVEL_COLUMN_CONFIG(impactLevels)
      })
      .setFilterOptions({
        ...AUTOCOMPLETE_FILTER_CONFIG,
        ...IMPACT_LEVEL_FILTER_CONFIG
      })
      .makeSelectable(this.gridProps.canEdit)
      .makeEditable(this.gridProps.canEdit)
      .makeReadOnly(!this.gridProps.canEdit)
      .withCellEditor()
      .setValueSetter(params => {
        if (!params.newValue) return false;
        let newVal = Enums.Translator.ImpactLevelShortToLong(+params.newValue.key);
        this.updateImpactLevel(params.data.id, newVal, params.data.projectId);
        return true;
      })
      .withCellRenderer(params => {
        const impact: FP.Entities.IImpactSummary = params.data;
        return impact.impactLevel > 0 ? Enums.Translator.ImpactLevel(impact.impactLevel) : null;
      });

    if (this.gridProps.canEdit) {
      model.makeDeletable();
    }

    return model.generateColumnOptions();
  };

  updateDynamicFieldValueString = customFieldId => async (entityId: number, valueString: string) => {
    const customFieldProvider = CustomFieldsApi;
    await customFieldProvider.updateImpactCustomFieldValue(
      this.organisationId,
      this.projectId,
      customFieldId,
      entityId,
      null,
      valueString
    );
  };

  updateDynamicFieldValueInt = customFieldId => async (entityId: number, valueInt: number) => {
    const customFieldProvider = CustomFieldsApi;
    await customFieldProvider.updateImpactCustomFieldValue(
      this.organisationId,
      this.projectId,
      customFieldId,
      entityId,
      valueInt,
      null
    );
  };

  updateName = async (entityId: number, text: string, projectId: number) => {
    await this.updateTextField(ImpactField.NAME, entityId, text, projectId);
    await this.reloadData();
  };
  updateStartDate = async (entityId: number, date: Date, projectId: number) => {
    await this.updateDateField(ImpactField.START_DATE, entityId, date, projectId);
    await this.reloadData();
  };
  updateEndDate = async (entityId: number, date: Date, projectId: number) => {
    await this.updateDateField(ImpactField.END_DATE, entityId, date, projectId);
    await this.reloadData();
  };
  updateProgressStatus = async (entityId: number, data: any, projectId: number) => {
    await this.updateIdField(ImpactField.PROGRESS_STATUS, entityId, data, projectId);
    await this.reloadData();
  };
  updateOwnerName = async (entityId: number, text: any, projectId: number) => {
    await this.updateIdField(ImpactField.OWNER, entityId, text, projectId);
    await this.reloadData();
  };

  updateImpactLevel = async (entityId: number, numeric: number, projectId: number) => {
    await this.updateNumericField(ImpactField.IMPACT_LEVEL, entityId, numeric, projectId);
    await this.reloadData();
  };
}
